import { setFulfilled, setPending, setRejected } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  GroupJoinedFulfilledAction,
  GroupJoinedUpdaterAction,
  GroupLeftFulfilledAction,
} from '../../domains/groupJoinLeave/actions.types';
import type {
  MyGroupsLoadFulfilledAction,
  MyGroupsLoadMoreFulfilledAction,
  MyGroupsLoadPendingAction,
  MyGroupsLoadRejectedAction,
} from '../../domains/myGroups/actions.types';
import type {
  NavigationGroupsLoadAction,
  NavigationGroupsLoadFulfilledAction,
  NavigationGroupsLoadPendingAction,
  NavigationGroupsLoadRejectedAction,
} from '../../domains/navigationGroupsLoad/actions.types';
import type {
  AddFavoriteCommunityFulfilledAction,
  RemoveFavoriteCommunityFulfilledAction,
} from '../../domains/viewerCommunityRelationships/actions.types';

import type { ViewerCommunityIdSets } from './types';

export const initialState: ViewerCommunityIdSets = {
  pending: false,
  fulfilled: undefined,
  rejected: undefined,
};

export type ViewerCommunityIdSetsAction =
  | NavigationGroupsLoadAction
  | MyGroupsLoadPendingAction
  | MyGroupsLoadFulfilledAction
  | MyGroupsLoadRejectedAction
  | MyGroupsLoadMoreFulfilledAction
  | AddFavoriteCommunityFulfilledAction
  | RemoveFavoriteCommunityFulfilledAction
  | GroupJoinedFulfilledAction
  | GroupJoinedUpdaterAction
  | GroupLeftFulfilledAction;

const addFavoriteFulfilled = (state: ViewerCommunityIdSets, action: AddFavoriteCommunityFulfilledAction) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      favoriteCommunityIds: [...state.fulfilled.favoriteCommunityIds, action.payload.id],
    },
  };
};

const removeFavoriteFulfilled = (state: ViewerCommunityIdSets, action: RemoveFavoriteCommunityFulfilledAction) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      favoriteCommunityIds: state.fulfilled.favoriteCommunityIds.filter((id) => id !== action.payload.id),
    },
  };
};

const groupJoinedFulfilled = (state: ViewerCommunityIdSets, action: GroupJoinedFulfilledAction) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      communityIds: [action.payload.groupId, ...state.fulfilled.communityIds],
    },
  };
};

const groupJoinedUpdater = (state: ViewerCommunityIdSets, action: GroupJoinedUpdaterAction) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      communityIds: [action.payload.groupId, ...state.fulfilled.communityIds],
    },
  };
};

const groupLeftFulfilled = (state: ViewerCommunityIdSets, action: GroupLeftFulfilledAction) => {
  if (!state.fulfilled) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      communityIds: state.fulfilled.communityIds.filter((x) => x !== action.payload.groupId),
      favoriteCommunityIds: state.fulfilled.favoriteCommunityIds.filter((x) => x !== action.payload.groupId),
    },
  };
};

const myGroupsLoadPending = (
  state: ViewerCommunityIdSets,
  action: MyGroupsLoadPendingAction
): ViewerCommunityIdSets => {
  // Only bridge the pending state when the nav has no data yet. A re-load while already fulfilled
  // (e.g. bootstrap NavigationGroups resolved first) keeps the existing list rather than flashing the skeleton.
  if (!action.payload.bridgeToViewerCommunityIdSets || state.fulfilled) {
    return state;
  }

  return setPending();
};

const myGroupsLoadRejected = (
  state: ViewerCommunityIdSets,
  action: MyGroupsLoadRejectedAction
): ViewerCommunityIdSets => {
  // Surface a MyGroups failure as a rejected nav (so the Retry path is reachable) only when there is no
  // bridged data yet. If the nav is already fulfilled, keep it rather than tearing it down on a failed reload.
  if (!action.payload.bridgeToViewerCommunityIdSets || state.fulfilled) {
    return state;
  }

  return setRejected(action.payload.error);
};

const myGroupsLoadFulfilled = (state: ViewerCommunityIdSets, action: MyGroupsLoadFulfilledAction) => {
  if (!action.payload.bridgeToViewerCommunityIdSets) {
    return state;
  }

  return setFulfilled(action.payload.entities.viewerCommunityIdSets);
};

const dedupe = (ids: readonly string[]): string[] => [...new Set(ids)];

const navigationGroupsLoadPending = (
  state: ViewerCommunityIdSets,
  action: NavigationGroupsLoadPendingAction
): ViewerCommunityIdSets => {
  // When LeftNavPagination is on, the nav is driven entirely by the paginated MyGroups bridge. Ignore the
  // bootstrap NavigationGroupsClients lifecycle so it can't flash the skeleton or clobber the bridged data.
  if (action.payload.isLeftNavPaginationEnabled) {
    return state;
  }

  return setPending();
};

const navigationGroupsLoadRejected = (
  state: ViewerCommunityIdSets,
  action: NavigationGroupsLoadRejectedAction
): ViewerCommunityIdSets => {
  if (action.payload.isLeftNavPaginationEnabled) {
    return state;
  }

  return setRejected(action.payload.error);
};

const navigationGroupsLoadFulfilled = (
  state: ViewerCommunityIdSets,
  action: NavigationGroupsLoadFulfilledAction
): ViewerCommunityIdSets => {
  if (action.payload.isLeftNavPaginationEnabled) {
    return state;
  }

  return setFulfilled(action.payload.entities.viewerCommunityIdSets);
};

const myGroupsLoadMoreFulfilled = (state: ViewerCommunityIdSets, action: MyGroupsLoadMoreFulfilledAction) => {
  if (!action.payload.bridgeToViewerCommunityIdSets || !state.fulfilled) {
    return state;
  }

  const { communityIds, favoriteCommunityIds } = action.payload.entities.viewerCommunityIdSets;

  return {
    ...state,
    fulfilled: {
      communityIds: dedupe([...state.fulfilled.communityIds, ...communityIds]),
      favoriteCommunityIds: dedupe([...state.fulfilled.favoriteCommunityIds, ...favoriteCommunityIds]),
    },
  };
};

const reducer: Reducer<ViewerCommunityIdSets, ViewerCommunityIdSetsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'NAVIGATION_GROUPS.LOAD_PENDING':
      return navigationGroupsLoadPending(state, action);
    case 'NAVIGATION_GROUPS.LOAD_REJECTED':
      return navigationGroupsLoadRejected(state, action);
    case 'NAVIGATION_GROUPS.LOAD_FULFILLED':
      return navigationGroupsLoadFulfilled(state, action);
    case 'MY_GROUPS.LOAD_PENDING':
      return myGroupsLoadPending(state, action);
    case 'MY_GROUPS.LOAD_REJECTED':
      return myGroupsLoadRejected(state, action);
    case 'MY_GROUPS.LOAD_FULFILLED':
      return myGroupsLoadFulfilled(state, action);
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return myGroupsLoadMoreFulfilled(state, action);
    case 'COMMUNITY.ADD_FAVORITE_FULFILLED':
      return addFavoriteFulfilled(state, action);
    case 'COMMUNITY.REMOVE_FAVORITE_FULFILLED':
      return removeFavoriteFulfilled(state, action);
    case 'GROUP.JOINED_FULFILLED':
      return groupJoinedFulfilled(state, action);
    case 'GROUP.JOINED_UPDATER':
      return groupJoinedUpdater(state, action);
    case 'GROUP.LEFT_FULFILLED':
      return groupLeftFulfilled(state, action);
    default:
      return state;
  }
};

export default reducer;
